import { RotateCcw, ShieldCheck } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import {
  useCompanyPolicy,
  useSavePolicy,
  useTeamPolicy,
  useTeams,
  useUserPolicy,
  useUsers,
  type PolicyOverrides,
  type PolicySettings,
  type PolicySource,
  type PolicyTarget,
} from "@/api/queries";
import { useMe } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, NativeSelect } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { errorText } from "@/lib/errors";

import {
  draftOf,
  formatPolicyValue,
  formatSource,
  invalidField,
  policyGroups,
  sameDraft,
  type Draft,
  type FieldSpec,
  type PolicyValue,
} from "./policy";

type Level = PolicyTarget["level"];

export function MonitoringPage() {
  const { t } = useTranslation();
  const me = useMe();
  const canEdit = me.permissions.manage_company;
  const [params, setParams] = useSearchParams();
  const level = (["company", "team", "user"] as const).find((l) => l === params.get("level")) ?? "company";
  const teams = useTeams();
  const users = useUsers();
  const teamId = params.get("team") ?? teams.data?.[0]?.id ?? "";
  const userId = params.get("user") ?? "";

  const select = (next: Record<string, string>) => setParams(next, { replace: true });

  return (
    <Card>
      <CardHeader className="gap-4">
        <div>
          <CardTitle>{t("settings.policy.title")}</CardTitle>
          <CardDescription className="max-w-2xl">{t("settings.policy.description")}</CardDescription>
        </div>
        {!canEdit && <Alert>{t("settings.policy.readOnly")}</Alert>}
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            aria-label={t("settings.policy.scope")}
            className="max-w-full overflow-x-auto"
            value={level}
            onChange={(l) => select({ level: l })}
            options={(["company", "team", "user"] as const).map((l) => ({
              value: l,
              label: t(`settings.policy.scopes.${l}`),
            }))}
          />
          {level === "team" && (teams.data?.length ?? 0) > 0 && (
            <NativeSelect
              aria-label={t("settings.policy.pickTeam")}
              className="h-9 w-full sm:w-auto sm:min-w-48"
              value={teamId}
              onChange={(e) => select({ level, team: e.target.value })}
            >
              {teams.data?.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </NativeSelect>
          )}
          {level === "user" && (
            <NativeSelect
              aria-label={t("settings.policy.pickUser")}
              className="h-9 w-full sm:w-auto sm:min-w-56"
              value={userId}
              onChange={(e) => select({ level, user: e.target.value })}
            >
              <option value="" disabled>
                {t("settings.policy.pickUser")}
              </option>
              {users.data
                ?.filter((u) => u.status !== "invited")
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
            </NativeSelect>
          )}
        </div>
      </CardHeader>
      <div className="border-t px-6 py-5">
        {level === "company" && <CompanyLevel canEdit={canEdit} />}
        {level === "team" &&
          (teams.isPending ? (
            <Skeleton className="h-64" />
          ) : teamId ? (
            <TeamLevel key={teamId} teamId={teamId} canEdit={canEdit} />
          ) : (
            <EmptyState icon={ShieldCheck} title={t("settings.policy.noTeams")} />
          ))}
        {level === "user" && userId && <UserLevel key={userId} userId={userId} canEdit={canEdit} />}
      </div>
    </Card>
  );
}

function CompanyLevel({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const policy = useCompanyPolicy();
  if (policy.isPending) return <Skeleton className="h-96" />;
  if (policy.isError) return <Alert variant="danger">{errorText(t, policy.error)}</Alert>;
  return (
    <PolicyForm
      target={{ level: "company" }}
      hint={t("settings.policy.companyHint")}
      saved={draftOf(policy.data.settings)}
      effective={policy.data.settings}
      canEdit={canEdit}
    />
  );
}

function TeamLevel({ teamId, canEdit }: { teamId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const policy = useTeamPolicy(teamId);
  if (policy.isPending) return <Skeleton className="h-96" />;
  if (policy.isError) return <Alert variant="danger">{errorText(t, policy.error)}</Alert>;
  const own = draftOf(policy.data.overrides);
  return (
    <PolicyForm
      target={{ level: "team", id: teamId }}
      hint={t("settings.policy.teamHint")}
      saved={own}
      inherited={policy.data.inherited}
      effective={policy.data.effective}
      source={(key) => (own[key] === undefined ? { level: "company" } : undefined)}
      canEdit={canEdit}
    />
  );
}

function UserLevel({ userId, canEdit }: { userId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const policy = useUserPolicy(userId);
  if (policy.isPending) return <Skeleton className="h-96" />;
  if (policy.isError) return <Alert variant="danger">{errorText(t, policy.error)}</Alert>;
  return (
    <PolicyForm
      target={{ level: "user", id: userId }}
      hint={t("settings.policy.userHint")}
      saved={draftOf(policy.data.overrides)}
      inherited={policy.data.inherited}
      effective={policy.data.effective}
      source={(key) => policy.data.sources[key]}
      canEdit={canEdit}
    />
  );
}

type PolicyFormProps = {
  target: PolicyTarget;
  hint: string;
  /** Values stored on this level. */
  saved: Draft;
  /** Values of the levels above; undefined for the company level. */
  inherited?: PolicySettings;
  effective: PolicySettings;
  /** Where the effective value comes from; undefined means this level. */
  source?: (key: keyof PolicySettings) => PolicySource | undefined;
  canEdit: boolean;
};

function PolicyForm({ target, hint, saved, inherited, effective, source, canEdit }: PolicyFormProps) {
  const { t } = useTranslation();
  const save = useSavePolicy();
  const [draft, setDraft] = useState<Draft>(saved);
  const [done, setDone] = useState(false);
  const dirty = !sameDraft(draft, saved);
  const invalid = invalidField(draft);
  const level: Level = target.level;

  const change = (key: keyof PolicySettings, value: PolicyValue | undefined) => {
    setDone(false);
    setDraft((d) => {
      const rest = Object.fromEntries(Object.entries(d).filter(([k]) => k !== key)) as Draft;
      return value === undefined ? rest : { ...rest, [key]: value };
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setDone(false);
    try {
      await save.mutateAsync({ target, values: draft as PolicyOverrides });
      setDone(true);
    } catch {
      // Shown below.
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-6">
      <p className="max-w-2xl text-muted">{hint}</p>
      {policyGroups.map((group) => (
        <fieldset key={group.key} className="grid gap-1">
          <legend className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
            {t(`settings.policy.groups.${group.key}`)}
          </legend>
          <div className="divide-y rounded-lg border">
            {group.fields.map((f) => (
              <PolicyRow
                key={f.key}
                spec={f}
                level={level}
                value={draft[f.key]}
                inherited={inherited?.[f.key]}
                effective={effective[f.key]}
                source={dirty ? undefined : source?.(f.key)}
                showEffective={level !== "company" && !dirty}
                invalid={invalid === f.key}
                canEdit={canEdit}
                onChange={(v) => change(f.key, v)}
              />
            ))}
          </div>
        </fieldset>
      ))}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!dirty || invalid !== undefined || save.isPending}>
            {t("common.save")}
          </Button>
          {level !== "company" && Object.keys(draft).length > 0 && (
            <Button type="button" variant="ghost" onClick={() => setDraft({})}>
              <RotateCcw />
              {t("settings.policy.resetAll")}
            </Button>
          )}
          {done && <span className="text-success">{t("settings.policy.saved")}</span>}
        </div>
      )}
      {save.isError && <Alert variant="danger">{errorText(t, save.error)}</Alert>}
    </form>
  );
}

type PolicyRowProps = {
  spec: FieldSpec;
  level: Level;
  value: PolicyValue | undefined;
  inherited: PolicyValue | undefined;
  effective: PolicyValue;
  source: PolicySource | undefined;
  showEffective: boolean;
  invalid: boolean;
  canEdit: boolean;
  onChange: (value: PolicyValue | undefined) => void;
};

function PolicyRow({
  spec,
  level,
  value,
  inherited,
  effective,
  source,
  showEffective,
  invalid,
  canEdit,
  onChange,
}: PolicyRowProps) {
  const { t } = useTranslation();
  const id = useId();
  const label = t(`settings.policy.fields.${spec.key}.label`);
  const overridden = value !== undefined;

  let control;
  if (!canEdit) {
    control = (
      <span className="font-medium">
        {formatPolicyValue(t, spec.key, level === "company" ? effective : (value ?? effective))}
      </span>
    );
  } else if (level === "company") {
    control = <CompanyControl id={id} spec={spec} label={label} value={value} onChange={onChange} />;
  } else if (spec.kind === "number") {
    control = (
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={spec.min}
        max={spec.max}
        className="w-28"
        aria-invalid={invalid || undefined}
        placeholder={inherited === undefined ? "" : String(inherited)}
        value={value === undefined ? "" : String(value)}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
      />
    );
  } else {
    const options = spec.kind === "bool" ? [true, false] : spec.options;
    control = (
      <NativeSelect
        id={id}
        className="h-9 w-full sm:w-auto sm:min-w-52"
        value={overridden ? String(value) : ""}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? undefined : (options.find((o) => String(o) === raw) as PolicyValue));
        }}
      >
        <option value="">
          {t("settings.policy.inherit", {
            value: inherited === undefined ? "" : formatPolicyValue(t, spec.key, inherited),
          })}
        </option>
        {options.map((o) => (
          <option key={String(o)} value={String(o)}>
            {formatPolicyValue(t, spec.key, o as PolicyValue)}
          </option>
        ))}
      </NativeSelect>
    );
  }

  return (
    <div className="grid min-w-0 gap-3 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="min-w-0">
        <label htmlFor={id} className="flex flex-wrap items-center gap-2 font-medium">
          {label}
          {level !== "company" && overridden && (
            <Badge variant="primary">{t("settings.policy.overridden")}</Badge>
          )}
        </label>
        <p className="text-xs text-muted">{t(`settings.policy.fields.${spec.key}.hint`)}</p>
        {showEffective && (
          <p className="mt-1 text-xs">
            <span className="text-muted">{t("settings.policy.effective")}: </span>
            <span className="font-medium">{formatPolicyValue(t, spec.key, effective)}</span>
            {source && <span className="text-muted"> · {formatSource(t, source)}</span>}
          </p>
        )}
      </div>
      <div className="max-w-full min-w-0 overflow-x-auto sm:justify-self-end">{control}</div>
    </div>
  );
}

function CompanyControl({
  id,
  spec,
  label,
  value,
  onChange,
}: {
  id: string;
  spec: FieldSpec;
  label: string;
  value: PolicyValue | undefined;
  onChange: (value: PolicyValue) => void;
}) {
  const { t } = useTranslation();
  switch (spec.kind) {
    case "bool":
      return <Switch id={id} checked={Boolean(value)} onCheckedChange={(v) => onChange(v)} />;
    case "number":
      return (
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          required
          min={spec.min}
          max={spec.max}
          className="w-28"
          value={value === undefined ? "" : String(value)}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      );
    case "choice":
      return (
        <Segmented
          aria-label={label}
          value={String(value)}
          onChange={(raw) => onChange(spec.options.find((o) => String(o) === raw) as PolicyValue)}
          options={spec.options.map((o) => ({
            value: String(o),
            label: formatPolicyValue(t, spec.key, o as PolicyValue),
          }))}
        />
      );
  }
}
